import initial_redirects from '@/config/redirects';
import { useEffect, useMemo, useRef, useState } from 'react';
import { LoadingSpinner } from '../LoadingSpinner';
import { useEditor } from '../context/EditorContext/EditorContext';
import { useNotifications } from '../context/NotificationContext/NotificationContext';
import { useDebounce } from '../hooks/useDebounce';
import { API } from '../utils/api';

const REDIRECTS_CACHE_KEY_PREFIX = 'editor:redirects';

function getRedirectsCacheKey(companyId) {
    return `${REDIRECTS_CACHE_KEY_PREFIX}:${companyId}`;
}

function normalizeRedirectRows(rows) {
    if (!Array.isArray(rows)) return [];
    return rows
        .filter((row) => row && typeof row === 'object')
        .map((row) => ({
            source: String(row.source || '').trim(),
            destination: String(row.destination || '').trim(),
            permanent: row.permanent !== false,
        }))
        .filter((row) => !!row.source && !!row.destination);
}

function mergeRedirectRows(primaryRows, secondaryRows) {
    const merged = [];
    const seen = new Set();
    [
        ...normalizeRedirectRows(primaryRows),
        ...normalizeRedirectRows(secondaryRows),
    ].forEach((row) => {
        const key = row.source.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        merged.push(row);
    });
    return merged;
}

function readCachedRedirectRows(companyId) {
    if (typeof window === 'undefined' || !companyId) return [];
    try {
        const raw = window.localStorage.getItem(
            getRedirectsCacheKey(companyId)
        );
        if (!raw) return [];
        return normalizeRedirectRows(JSON.parse(raw));
    } catch {
        return [];
    }
}

function writeCachedRedirectRows(companyId, rows) {
    if (typeof window === 'undefined' || !companyId) return;
    try {
        window.localStorage.setItem(
            getRedirectsCacheKey(companyId),
            JSON.stringify(normalizeRedirectRows(rows))
        );
    } catch {
        // ignore localStorage write issues
    }
}

/* ------------ lightweight collapsible (Head-style header) ------------ */
function Collapsible({
    defaultOpen = false,
    titleOpen,
    titleClosed,
    children,
}) {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div className="rounded-xl border border-neutral-200 bg-white shadow-sm">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="group flex w-full items-center justify-between px-4 py-3 text-left"
            >
                <span className="text-[14px] font-medium leading-5 text-neutral-600 group-hover:text-neutral-700">
                    {open ? titleOpen : titleClosed}
                </span>
                <i
                    className={`fas fa-chevron-down text-neutral-400 transition-transform group-hover:text-neutral-500 ${
                        open ? 'rotate-180' : ''
                    }`}
                    aria-hidden="true"
                />
            </button>

            {open && <div className="h-px w-full bg-neutral-200" />}

            <div className={open ? 'block' : 'hidden'}>
                <div className="px-4 py-3">{children}</div>
            </div>
        </div>
    );
}

/* ----------------------- small CSV utilities ----------------------- */
const splitCSVLine = (line) =>
    line
        .split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/)
        .map((s) => s.trim().replace(/^"(.*)"$/, '$1'));

const ensurePath = (val) => {
    if (!val) return null;
    const raw = String(val).trim();
    if (!raw) return null;
    try {
        const u = new URL(raw);
        const p = (u.pathname || '/').replace(/\/+$/, '') || '/';
        return p || '/';
    } catch {
        let p = raw;
        if (!p.startsWith('/')) p = '/' + p;
        p = p.replace(/\/+$/, '') || '/';
        return p || '/';
    }
};

/* ---------------- Autocomplete “To” field (overlay gradient dropdown) ---------------- */
function SuggestInput({ value, onChange, options, placeholder = '/new-path' }) {
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(0);
    const inputRef = useRef(null);
    const hostRef = useRef(null);

    // compact sizing
    const INPUT_H = 22; // inner input height (change here)
    const RING = 2; // gradient ring thickness
    const SPACER_H = INPUT_H + RING * 2;

    const filtered = useMemo(() => {
        const v = (value ?? '').toLowerCase();
        const list = (options ?? []).filter((o) =>
            String(o).toLowerCase().includes(v)
        );
        const uniq = Array.from(new Set(list));
        // keep "/" first
        uniq.sort((a, b) => (a === '/' ? -1 : b === '/' ? 1 : 0));
        return uniq.slice(0, 200); // list can be long; scroll caps visible items
    }, [options, value]);

    useEffect(() => {
        if (!open) setHighlight(0);
    }, [open]);

    useEffect(() => {
        const onDocClick = (ev) => {
            if (!hostRef.current) return;
            if (!hostRef.current.contains(ev.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    const select = (val) => {
        onChange(val);
        setOpen(false);
        requestAnimationFrame(() => inputRef.current?.focus());
    };

    const onKeyDown = (e) => {
        if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            setOpen(true);
            return;
        }
        if (!open) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((i) =>
                Math.min(i + 1, Math.max(filtered.length - 1, 0))
            );
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filtered[highlight]) select(filtered[highlight]);
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    return (
        // allow shrinking: w-0 flex-1 min-w-0; raise stacking when open
        <div
            ref={hostRef}
            className={`relative w-0 min-w-0 flex-1 ${open ? 'z-[999]' : ''}`}
        >
            {/* spacer so layout doesn’t move; the real UI is absolutely positioned */}
            <div style={{ height: `${SPACER_H}px` }} aria-hidden="true" />

            <div
                className={`absolute inset-x-0 top-0 ${
                    open ? 'z-[1000]' : 'z-40'
                }`}
            >
                {/* gradient wrapper becomes the unified border (input + dropdown) */}
                <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                    <div className="overflow-hidden rounded-[10px] bg-white">
                        {/* input */}
                        <div className="flex h-[22px] items-stretch">
                            <input
                                ref={inputRef}
                                className="flex-1 appearance-none border-0 bg-transparent px-1 text-[10px] leading-5
                           text-neutral-800 placeholder:text-neutral-400
                           focus:border-transparent focus:outline-none focus:ring-0 focus:ring-offset-0"
                                placeholder={placeholder}
                                value={value}
                                onFocus={() => setOpen(true)}
                                onChange={(e) => {
                                    onChange(e.target.value);
                                    setOpen(true);
                                }}
                                onKeyDown={onKeyDown}
                            />
                        </div>

                        {/* dropdown: ~5 items visible, then scroll */}
                        {open && filtered.length > 0 && (
                            <ul className="max-h-[120px] overflow-y-auto overscroll-contain py-1">
                                {filtered.map((opt, i) => (
                                    <li
                                        key={opt}
                                        className={`cursor-pointer px-1 py-0.5 text-[10px] ${
                                            i === highlight
                                                ? 'bg-neutral-50 text-neutral-900'
                                                : 'text-neutral-800 hover:bg-neutral-50'
                                        }`}
                                        onMouseDown={(e) => {
                                            e.preventDefault(); // keep focus
                                            select(opt);
                                        }}
                                        onMouseEnter={() => setHighlight(i)}
                                    >
                                        {opt}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ---- folders to collapse into wildcard rules -> "/" ---- */
const FOLDER_PREFIXES = [
    '/blog',
    '/category',
    '/categories',
    '/tag',
    '/tags',
    '/author',
    '/authors',
    '/news',
    '/press',
    '/archive',
    '/archives',
];

async function fetchSiteRoutes() {
    const response = await fetch('/api/site-routes', {
        cache: 'no-store',
    });
    if (!response.ok) {
        throw new Error('Failed to load site routes');
    }
    const payload = await response.json();
    return Array.isArray(payload?.pages) ? payload.pages : [];
}

export function Redirects() {
    const notifications = useNotifications();
    const editor = useEditor();
    const { company_id } = editor.global.data;

    const [saved_redirects, setSavedRedirects] = useState(
        initial_redirects || []
    );
    const [redirects, setRedirects] = useState(initial_redirects || []);
    const debounced_redirects = useDebounce(redirects, 1000);

    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(false);
    const [notified, setNotified] = useState(false);

    const [loadingLinks, setLoadingLinks] = useState(false);
    const [baseUrl, setBaseUrl] = useState('');
    const urlInputRef = useRef(null);

    const [query, setQuery] = useState('');
    const csvInputRef = useRef(null);

    const [destinations, setDestinations] = useState(['/']);

    useEffect(() => {
        let cancelled = false;

        const loadRedirects = async () => {
            const cachedRedirects = readCachedRedirectRows(company_id);
            if (!cancelled && cachedRedirects.length) {
                setRedirects(cachedRedirects);
                setSavedRedirects(cachedRedirects);
                setSaved(true);
                setError(false);
            }

            try {
                const serverRedirects = await API.get(
                    `${company_id}/redirects`
                );
                if (cancelled || !Array.isArray(serverRedirects)) return;

                const mergedRedirects = mergeRedirectRows(
                    serverRedirects,
                    cachedRedirects
                );
                setRedirects(mergedRedirects);
                setSavedRedirects(mergedRedirects);
                writeCachedRedirectRows(company_id, mergedRedirects);
                setSaved(true);
                setError(false);
            } catch {
                // Keep local fallback/cache when API fetch fails.
            }
        };

        loadRedirects();

        return () => {
            cancelled = true;
        };
    }, [company_id]);

    /* ---------------- helpers ---------------- */
    const normalizeUrl = (url) => {
        if (!url) return '';
        let clean = String(url).trim();
        if (!/^https?:\/\//i.test(clean)) clean = 'https://' + clean;
        return clean.replace(/\/+$/, '');
    };

    const toPath = (link, mainHost) => {
        try {
            const u = new URL(link);
            if (
                u.hostname.replace(/^www\./, '') !==
                mainHost.replace(/^www\./, '')
            )
                return null;
            const path = u.pathname.replace(/\/+$/, '') || '/';
            return path;
        } catch {
            const p = String(link || '').trim();
            if (!p) return null;
            if (!p.startsWith('/')) return '/' + p;
            const tidy = p.replace(/\/+$/, '') || '/';
            return tidy;
        }
    };

    const focusUrlInput = () => {
        if (!urlInputRef.current) return;
        if (document.activeElement !== urlInputRef.current) {
            urlInputRef.current.focus();
            const v = urlInputRef.current.value || '';
            urlInputRef.current.setSelectionRange(v.length, v.length);
        }
    };

    /* --- load current site routes for destination suggestions --- */
    useEffect(() => {
        const load = async () => {
            try {
                const pages = await fetchSiteRoutes();
                const set = new Set(['/']);
                pages.forEach((p) => {
                    if (typeof p === 'string' && p.trim()) {
                        let v = p.trim();
                        if (!v.startsWith('/')) v = '/' + v;
                        v = v.replace(/\/+$/, '') || '/';
                        set.add(v);
                    }
                });
                setDestinations(Array.from(set));
            } catch {
                /* keep default */
            }
        };
        load();
    }, []);

    useEffect(() => {
        const handleExternalRedirectsUpdate = (event) => {
            const incoming = event?.detail;
            if (!Array.isArray(incoming)) return;
            const normalizedIncoming = normalizeRedirectRows(incoming);
            setRedirects(normalizedIncoming);
            setSavedRedirects(normalizedIncoming);
            writeCachedRedirectRows(company_id, normalizedIncoming);
            setSaved(true);
            setError(false);
            setSaving(false);
            setNotified(false);
        };

        window.addEventListener(
            'editor:redirects-updated',
            handleExternalRedirectsUpdate
        );
        return () => {
            window.removeEventListener(
                'editor:redirects-updated',
                handleExternalRedirectsUpdate
            );
        };
    }, [company_id]);

    /* ---------------- fetch sitemap ---------------- */
    const fetchLinks = async () => {
        const normalized = normalizeUrl(baseUrl);
        if (!normalized) return;

        setLoadingLinks(true);
        try {
            const res = await fetch('/api/scrape-links', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ baseUrl: normalized }),
            });

            const data = await res.json();

            if (!res.ok) {
                notifications.push({
                    variant: 'error',
                    title: data?.error || 'Failed to fetch sitemap.xml',
                });
                return;
            }

            const host = new URL(normalized).hostname.replace(/^www\./, '');
            let incomingPaths = Array.from(
                new Set(
                    (data.links || [])
                        .map((l) => toPath(l, host))
                        .filter(Boolean)
                )
            );

            incomingPaths = incomingPaths.filter(
                (p) => !/\.xml(\.gz)?$/i.test(p)
            );

            if (!incomingPaths.length) {
                notifications.push({
                    variant: 'error',
                    title: 'No links found in sitemap.',
                });
                return;
            }

            const existingSources = new Set(
                redirects.map((r) => (r?.source || '').trim()).filter(Boolean)
            );

            const wildcardsToAdd = [];
            const skipPrefixes = new Set();
            for (const prefix of FOLDER_PREFIXES) {
                if (
                    incomingPaths.some(
                        (p) => p === prefix || p.startsWith(prefix + '/')
                    )
                ) {
                    const wildcard = `${prefix}/:path*`;
                    if (!existingSources.has(wildcard)) {
                        wildcardsToAdd.push({
                            source: wildcard,
                            destination: '/',
                            permanent: true,
                        });
                    }
                    skipPrefixes.add(prefix);
                }
            }
            const shouldSkip = (p) =>
                [...skipPrefixes].some(
                    (pref) => p === pref || p.startsWith(pref + '/')
                );

            const newRows = incomingPaths
                .filter((p) => !shouldSkip(p))
                .filter((p) => !existingSources.has(p))
                .map((p) => ({ source: p, destination: '/', permanent: true }));

            const rowsToAdd = [...wildcardsToAdd, ...newRows];

            if (rowsToAdd.length) {
                setRedirects((cur) => [...cur, ...rowsToAdd]);
                notifications.push({
                    variant: 'success',
                    title: `Added ${rowsToAdd.length} redirect${
                        rowsToAdd.length > 1 ? 's' : ''
                    }`,
                });
            } else {
                notifications.push({
                    variant: 'success',
                    title: 'All found links already listed.',
                });
            }
        } catch {
            notifications.push({
                variant: 'error',
                title: 'Failed to fetch sitemap.',
            });
        } finally {
            setLoadingLinks(false);
            focusUrlInput();
        }
    };

    /* ---------------- CSV upload ---------------- */
    const handleCSVClick = () => csvInputRef.current?.click();

    const handleCSVUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const text = await file.text();
            const lines = text
                .split(/\r?\n/)
                .filter((l) => l.trim().length > 0);

            if (!lines.length) {
                notifications.push({
                    variant: 'error',
                    title: 'CSV is empty.',
                });
                e.target.value = '';
                return;
            }

            const headerCells = splitCSVLine(lines[0]);
            let startIndex = 0;
            let colFrom = 0;
            let colTo = 1;

            const lower = headerCells.map((h) => h.toLowerCase());
            const hasHeader =
                lower.includes('from') ||
                lower.includes('to') ||
                lower.includes('source') ||
                lower.includes('destination');

            if (hasHeader) {
                const idxFrom = Math.max(
                    lower.indexOf('from'),
                    lower.indexOf('source')
                );
                const idxTo = Math.max(
                    lower.indexOf('to'),
                    lower.indexOf('destination')
                );
                if (idxFrom >= 0) colFrom = idxFrom;
                if (idxTo >= 0) colTo = idxTo;
                startIndex = 1;
            }

            const rows = [];
            for (let i = startIndex; i < lines.length; i++) {
                const cells = splitCSVLine(lines[i]);
                const fromVal = ensurePath(cells[colFrom]);
                const toVal = ensurePath(cells[colTo]) || '/';
                if (fromVal)
                    rows.push({
                        source: fromVal,
                        destination: toVal,
                        permanent: true,
                    });
            }

            if (!rows.length) {
                notifications.push({
                    variant: 'error',
                    title: 'No valid rows found in CSV.',
                });
                e.target.value = '';
                return;
            }

            const existingPairs = new Set(
                redirects
                    .filter((r) => r.source && r.destination)
                    .map((r) => `${r.source}→${r.destination}`)
            );

            const deduped = [];
            let skipped = 0;

            for (const r of rows) {
                const key = `${r.source}→${r.destination}`;
                if (existingPairs.has(key)) {
                    skipped++;
                } else {
                    existingPairs.add(key);
                    deduped.push(r);
                }
            }

            if (deduped.length) {
                setRedirects((cur) => [...cur, ...deduped]);
                notifications.push({
                    variant: 'success',
                    title: `Imported ${deduped.length} redirect${
                        deduped.length > 1 ? 's' : ''
                    }${
                        skipped
                            ? ` (${skipped} duplicate${
                                  skipped > 1 ? 's' : ''
                              } skipped)`
                            : ''
                    }`,
                });
            } else {
                notifications.push({
                    variant: 'success',
                    title: `All ${rows.length} row${
                        rows.length > 1 ? 's' : ''
                    } already exist.`,
                });
            }
        } catch {
            notifications.push({
                variant: 'error',
                title: 'Failed to read CSV file.',
            });
        } finally {
            e.target.value = '';
            focusUrlInput();
        }
    };

    /* ---------------- autosave ---------------- */
    useEffect(() => {
        if (
            JSON.stringify(saved_redirects) ===
            JSON.stringify(debounced_redirects)
        )
            return;

        const valid = debounced_redirects.every(
            (r) => r.source !== '' && r.destination !== ''
        );
        if (!valid) return;

        setSaved(false);
        setError(false);
        setSaving(true);

        const saveRedirects = async () => {
            try {
                await API.post(`${company_id}/redirects`, {
                    redirects: debounced_redirects,
                });
                setSavedRedirects(debounced_redirects);
                writeCachedRedirectRows(company_id, debounced_redirects);
                setSaved(true);
                if (notified) setNotified(false);
            } catch {
                if (!notified) {
                    notifications.push({
                        variant: 'error',
                        title: 'Failed to save redirects!',
                    });
                }
                setError(true);
            } finally {
                setSaving(false);
            }
        };

        saveRedirects();
    }, [debounced_redirects]);

    /* ---------------- list handlers ---------------- */
    const handleRedirectChange = ({ index, source, destination }) => {
        setRedirects((arr) =>
            arr.map((r, i) =>
                i === index ? { ...r, source, destination, permanent: true } : r
            )
        );
    };

    const handleAddRedirect = () => {
        setRedirects((arr) => [
            ...arr,
            { source: '', destination: '/', permanent: true },
        ]);
    };

    const handleRemoveRedirect = ({ index }) => {
        setRedirects((arr) => arr.filter((_, i) => i !== index));
    };

    const incompleteCount = redirects.reduce(
        (n, r) => (r.source && r.destination ? n : n + 1),
        0
    );

    // filter view (preserve original indexes)
    const visible = redirects
        .map((r, i) => ({ r, i }))
        .filter(({ r }) => {
            if (!query.trim()) return true;
            const q = query.toLowerCase();
            return (
                String(r.source || '')
                    .toLowerCase()
                    .includes(q) ||
                String(r.destination || '')
                    .toLowerCase()
                    .includes(q)
            );
        });

    /* ---------------- render ---------------- */
    return (
        <div className="my-4">
            {/* Always-visible: URL + buttons */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
                <div className="min-w-[240px] flex-1 rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                    <div className="flex h-[30px] items-stretch overflow-hidden rounded-[10px] bg-white">
                        <input
                            ref={urlInputRef}
                            type="text"
                            spellCheck={false}
                            placeholder="Enter old URL"
                            className="flex-1 appearance-none border-0 bg-transparent px-2 text-[12px] leading-6 text-neutral-800 placeholder:text-neutral-400
                         focus:border-transparent focus:outline-none focus:ring-0 focus:ring-offset-0"
                            value={baseUrl}
                            onChange={(e) => setBaseUrl(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && fetchLinks()}
                            aria-label="Old website URL"
                        />
                    </div>
                </div>

                <button
                    type="button"
                    onClick={fetchLinks}
                    onMouseDown={(e) => e.preventDefault()}
                    className="flex h-[30px] items-center justify-center rounded-[10px] bg-primary-700 px-3 text-[12px] font-semibold text-white shadow-sm transition-colors duration-150 ease-in-out hover:bg-primary-800 disabled:opacity-50"
                    disabled={loadingLinks}
                    title="Fetch links from sitemap.xml"
                >
                    {loadingLinks ? (
                        <span className="mr-2 inline-flex items-center">
                            <LoadingSpinner size="small" />
                        </span>
                    ) : (
                        <i className="fas fa-download mr-2" />
                    )}
                    Fetch Links
                </button>

                <button
                    type="button"
                    onClick={handleCSVClick}
                    className="flex h-[30px] items-center justify-center rounded-[10px] bg-white px-3 text-[12px] font-semibold text-primary-700 shadow-sm ring-1 ring-inset ring-primary-600 transition-colors duration-150 hover:bg-primary-50"
                    title="Import redirects from a CSV file"
                >
                    <i className="fas fa-file-arrow-up mr-2" />
                    Upload .CSV
                </button>
                <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="sr-only"
                    onChange={handleCSVUpload}
                />
            </div>

            {/* Collapsible list */}
            <Collapsible
                titleOpen="Hide Redirects"
                titleClosed="Show Redirects"
                defaultOpen={false}
            >
                {/* Search + status row */}
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-[180px] flex-1 rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                        <div className="flex h-[26px] items-stretch overflow-hidden rounded-[10px] bg-white">
                            <input
                                type="text"
                                spellCheck={false}
                                placeholder="Search redirects…"
                                className="flex-1 appearance-none border-0 bg-transparent px-2 text-[11px] leading-5 text-neutral-800 placeholder:text-neutral-400
                           focus:border-transparent focus:outline-none focus:ring-0 focus:ring-offset-0"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                aria-label="Search redirects"
                            />
                        </div>
                    </div>

                    {incompleteCount > 0 ? (
                        <div className="text-[11px] text-neutral-500">
                            Auto-save paused — {incompleteCount} incomplete row
                            {incompleteCount > 1 ? 's' : ''}
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            {saving && <LoadingSpinner />}
                            {saved && (
                                <i
                                    className="fas fa-check fa-lg text-green-600"
                                    title="saved"
                                />
                            )}
                            {error && (
                                <i
                                    className="fas fa-xmark fa-lg text-red-600"
                                    title="failed to save"
                                />
                            )}
                        </div>
                    )}
                </div>

                {/* Redirect list — compact, shrinkable fields */}
                <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-center gap-1 px-0.5">
                        <label className="w-full text-[10px] text-neutral-500">
                            From
                        </label>
                        <div className="w-5" />
                        <label className="w-full text-[10px] text-neutral-500">
                            To
                        </label>
                        <div className="w-5" />
                    </div>

                    {visible.map(({ r: redirect, i: index }) => (
                        <div
                            key={index}
                            className="flex min-w-0 items-center gap-1"
                        >
                            {/* FROM (shrinks) */}
                            <div className="w-0 min-w-0 flex-1 rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                <div className="flex h-[22px] items-stretch overflow-hidden rounded-[10px] bg-white">
                                    <input
                                        className="flex-1 appearance-none border-0 bg-transparent px-1 text-[10px] leading-5 text-neutral-800 placeholder:text-neutral-400
                               focus:border-transparent focus:outline-none focus:ring-0 focus:ring-offset-0"
                                        placeholder="/from-path"
                                        value={redirect.source}
                                        onChange={(e) =>
                                            handleRedirectChange({
                                                index,
                                                source: e.target.value,
                                                destination:
                                                    redirect.destination,
                                            })
                                        }
                                    />
                                </div>
                            </div>

                            {/* arrow (doesn't shrink) */}
                            <i className="fas fa-arrow-right fa-xs w-5 shrink-0 text-center text-neutral-500" />

                            {/* TO (shrinks) */}
                            <SuggestInput
                                value={redirect.destination}
                                onChange={(v) =>
                                    handleRedirectChange({
                                        index,
                                        source: redirect.source,
                                        destination: v,
                                    })
                                }
                                options={destinations}
                                placeholder="/new-path"
                            />

                            {/* trash (doesn't shrink) */}
                            <button
                                type="button"
                                className="h-[22px] shrink-0 rounded-[10px] px-1 text-neutral-600 hover:text-red-500"
                                onClick={() => handleRemoveRedirect({ index })}
                                title="Remove redirect"
                            >
                                <i className="fas fa-trash fa-xs w-5" />
                            </button>
                        </div>
                    ))}

                    <div className="mt-2 flex w-full items-center justify-center">
                        <button
                            type="button"
                            onClick={handleAddRedirect}
                            className="text-neutral-700 hover:text-primary-600"
                        >
                            <i className="far fa-plus-circle fa-lg mr-2 cursor-pointer" />
                            <span className="text-[11px] font-semibold">
                                Add redirect
                            </span>
                        </button>
                    </div>
                </div>
            </Collapsible>
        </div>
    );
}
